import React, { ChangeEvent, useState } from "react";
import { MapPosition } from "../common/types";
import { checkLatitude, checkLongitude } from "../common/validators/coordValidator";
import { isNumeric } from "../common/validators/numberValidator";
import { debounce } from "lodash";
import { Tooltip } from "../core/core-widgets/tooltips/Tooltip";
import { GlyphButton } from "../core/core-widgets/buttons/GlyphButton";
import { GlyphIcon } from "../core/core-widgets/icons/glyphIcons";
import { ThemeColor } from "../core/core-widgets";

const INPUT_CHANGED_DEBOUNCE_TIME_MS = 1000;

export interface CenterMapDispatchProps {
    panTo: (newPosition: MapPosition) => void;
    showError: (title: string, message: string) => void;
    shouldRenderDot: (renderDot: boolean) => void;
}

export interface CenterMapStateProps {
    position?: MapPosition;
}

type CenterProps = CenterMapDispatchProps & CenterMapStateProps;

export const CenterMap = (props: CenterProps) => {
    const { position, panTo, showError, shouldRenderDot } = props;
    const [positionX, setPositionX] = useState<string | undefined>(undefined);
    const [positionY, setPositionY] = useState<string | undefined>(undefined);
    const [showInputs, setShowInputs] = useState<boolean>(false);

    React.useEffect(() => {
        setPositionX(position?.x.toFixed(6));
        setPositionY(position?.y.toFixed(6));
    }, [position?.x, position?.y]);

    const updatePosition = (x: number, y: number) => {
        console.log("Updating position", x, y);
        if (!checkLatitude(x) || !checkLongitude(y)) {
            showError("Invalid coordinates", `Entered coordinates are not valid. [${x},${y}]`);
            return;
        }
        panTo({ x: x, y: y, crs: position!.crs });
    };

    const debouncedUpdatePosition = debounce(
        (x: number, y: number) => updatePosition(x, y),
        INPUT_CHANGED_DEBOUNCE_TIME_MS
    );

    const handlePositionX = (e: ChangeEvent<HTMLInputElement>) => {
        setPositionX(e.target.value);
        if (isNumeric(e.target.value) && isNumeric(positionY)) {
            debouncedUpdatePosition(e.target.value, positionY!);
        }
    };

    const handlePositionY = (e: ChangeEvent<HTMLInputElement>) => {
        setPositionY(e.target.value);
        if (isNumeric(e.target.value) && isNumeric(positionX)) {
            debouncedUpdatePosition(positionX!, e.target.value);
        }
    };

    const handleToggleButtonClick = () => {
        shouldRenderDot(!showInputs);
        setShowInputs(!showInputs);
    };

    return (
        <div>
            {showInputs && (
                <>
                    <input
                        id={"inputPositionY"}
                        step={"any"}
                        type={"number"}
                        value={positionY}
                        onChange={handlePositionY}
                    />
                    <input
                        id={"inputPositionX"}
                        step={"any"}
                        type={"number"}
                        value={positionX}
                        onChange={handlePositionX}
                    />
                </>
            )}
            <Tooltip body={"Show/Hide map center tool"}>
                <GlyphButton
                    style={{
                        width: "25px",
                        height: "25px",
                    }}
                    id={"toggleVisibilityButton"}
                    color={ThemeColor.PRIMARY}
                    onClick={() => handleToggleButtonClick()}
                    glyph={GlyphIcon.CENTER_MAP}
                />
            </Tooltip>
        </div>
    );
};
